import React, { useState } from 'react'
import ColorPickerV2 from 'src/components/common/ColorPickerV2'
import { InputSlider } from 'src/components/common/slider/InputSlider'
import {
    LinkButton,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@shopflo/ui'
import { BorderSides, BORDER_SIDES_OPTIONS } from '../config'
import { useCartContext } from 'src/lib/contexts/CartContext'
import { useCheckoutUI } from 'src/lib/contexts/CheckoutUIContext'
import { SLIDER_CONSTANTS } from 'src/lib/utils/constants'
import { setUIAttributes } from 'src/lib/core/api-methods'
import { createUIAttributePayload, SFContextType } from 'src/lib/utils/attributesParsers'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { useLocale } from 'src/lib/hooks/useLocal'
import { classNames } from 'src/lib/utils/helpers'

interface UpsellConfigType {
    backgroundColor: string
    borderColor: string
    buttonColor: string
    textColor: string
    titleColor: string
    offerBadgeColor: string
    cardRadius: number
    cardBorderWidth: number
    cardBorderSides: BorderSides
}

export const UpsellControl = () => {
    const {
        getters: { cartUIData },
        actions: { setCartUIConfigData }
    } = useCartContext()
    const {
        getters: { checkoutUI },
        actions: { updateCheckoutUI }
    } = useCheckoutUI()
    const { t } = useLocale()
    const [isActivating, setIsActivating] = useState(false)

    const isUpsellActivated = Boolean(checkoutUI?.upSellConfig?.isEnabled)

    const handleConfigChange = (updates: Partial<UpsellConfigType>) => {
        setCartUIConfigData({ ...cartUIData.upsell, ...updates }, 'UPSELL')
    }

    const handleActivateUpsell = async () => {
        try {
            setIsActivating(true)
            const upsellConfig = {
                upsell: {
                    is_enabled: true,
                    upsell_title: checkoutUI?.upSellConfig?.upSellTitle ?? '',
                    discounted_upsell_title: checkoutUI?.upSellConfig?.discountedUpSellTitle ?? ''
                }
            }

            const payload = createUIAttributePayload(SFContextType.CART, { ...upsellConfig })

            const [response]: any = await Promise.all([
                updateCheckoutUI(upsellConfig),
                setUIAttributes(payload)
            ])

            if (response?.error) {
                errorToast(response.error)
                return
            }

            successToast(t('upsell_activated_success'))
        } catch (err) {
            console.error(err)
            errorToast(t('something_went_wrong'))
        } finally {
            setIsActivating(false)
        }
    }

    return (
        <div className='flex flex-col gap-2'>
            {!isUpsellActivated && (
                <div className='flex w-full flex-row items-start pt-0 px-2 pb-3'>
                    <div className='flex flex-row items-center gap-1 text-text-xs-medium text-yellow-600'>
                        <span>{t('upsell_inactive_in_dashboard')}</span>
                        <LinkButton
                            size='xs'
                            onClick={(e) => {
                                if (isActivating) {
                                    e.preventDefault()
                                    return
                                }
                                handleActivateUpsell()
                            }}
                            aria-disabled={isActivating}
                            className={isActivating ? 'opacity-40 pointer-events-none' : ''}
                        >
                            {isActivating ? t('upsell_activating') : t('upsell_activate_now')}
                        </LinkButton>
                    </div>
                </div>
            )}

            <div className={classNames('flex flex-col gap-5', !isUpsellActivated ? 'pointer-events-none select-none opacity-40' : '')}>
            {/* Color Pickers - 2x3 Grid */}
            <div className='flex flex-col gap-2'>
                <div className='grid grid-cols-2 gap-2'>
                    <ColorPickerV2
                        label='Background'
                        color={cartUIData.upsell.backgroundColor}
                        onChange={(color) => handleConfigChange({ backgroundColor: color })}
                    />
                    <ColorPickerV2
                        label='Border'
                        color={cartUIData.upsell.borderColor}
                        onChange={(color) => handleConfigChange({ borderColor: color })}
                    />
                </div>
                <div className='grid grid-cols-2 gap-2'>
                    <ColorPickerV2
                        label='Button'
                        color={cartUIData.upsell.buttonColor}
                        onChange={(color) => handleConfigChange({ buttonColor: color })}
                    />
                    <ColorPickerV2
                        label='Text'
                        color={cartUIData.upsell.textColor}
                        onChange={(color) => handleConfigChange({ textColor: color })}
                    />
                </div>
                <div className='grid grid-cols-2 gap-2'>
                    <ColorPickerV2
                        label='Title'
                        color={cartUIData.upsell.titleColor}
                        onChange={(color) => handleConfigChange({ titleColor: color })}
                    />
                    <ColorPickerV2
                        label='Offer badge'
                        color={cartUIData.upsell.offerBadgeColor}
                        onChange={(color) => handleConfigChange({ offerBadgeColor: color })}
                    />
                </div>
            </div>

            {/* Card Radius */}
            <InputSlider
                label='Card radius'
                value={cartUIData.upsell.cardRadius}
                onChange={(value) => handleConfigChange({ cardRadius: value })}
                min={0}
                max={SLIDER_CONSTANTS.MAX_RADIUS}
                step={1}
                showDots={true}
                dotCount={5}
                unit='px'
            />

            {/* Card Border Width */}
            <div className='flex flex-col gap-1'>
                <InputSlider
                    label='Card border'
                    value={cartUIData.upsell.cardBorderWidth}
                    onChange={(value) => handleConfigChange({ cardBorderWidth: value })}
                    min={0}
                    max={SLIDER_CONSTANTS.MAX_BORDER_WIDTH}
                    step={1}
                    showDots={true}
                    dotCount={5}
                    unit='px'
                />

                {/* Card Border Sides Selector */}
                <div className='flex items-center justify-end gap-2 mt-1'>
                    <Select
                        value={cartUIData.upsell.cardBorderSides}
                        onValueChange={(value: BorderSides) => handleConfigChange({ cardBorderSides: value })}
                    >
                        <SelectTrigger className='w-[178px] h-8 bg-white'>
                            <SelectValue placeholder='Select sides' />
                        </SelectTrigger>
                        <SelectContent>
                            {BORDER_SIDES_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            </div>
        </div>
    )
}
